import { useEffect, useId, useState, type FocusEvent } from 'react'
import { ClipboardList } from 'lucide-react'
import type { ChecklistItem } from '~/api/types'
import { splitChecklistDetail } from './utils'

/**
 * '체크리스트 기준' 호버/포커스 패널.
 *
 * 조치 필요 카드에서 실제 항목 기준(기준·관리·현장 등)을 확인하도록, 마우스를 올리거나
 * 포커스하면(터치는 클릭 토글) 기준 섹션을 띄운다.
 *
 * WCAG 2.1 AA(1.4.13 Content on Hover or Focus) 충족:
 * - Dismissable: 포커스·포인터를 옮기지 않아도 Esc로 닫는다.
 * - Hoverable: 패널이 트리거와 같은 컨테이너 안에 있어, 패널로 마우스를 옮겨도 유지된다.
 * - Persistent: hover/focus가 유지되는 동안(또는 Esc 전까지) 사라지지 않는다.
 */
export function CriteriaHoverTab({ item }: { item?: ChecklistItem }) {
    const [open, setOpen] = useState(false)
    const panelId = useId()

    // Dismissable(WCAG 1.4.13): 열려 있는 동안 Esc로 닫는다 — 마우스 hover만으로 열렸을 때도
    // 포커스를 옮기지 않고 닫을 수 있도록 문서 레벨에서 듣는다.
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open])

    if (!item) return null
    const sections = splitChecklistDetail(item)

    // 컨테이너 밖으로 포커스가 완전히 나갈 때만 닫는다(트리거→패널 이동은 유지).
    const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setOpen(false)
        }
    }

    return (
        <div
            className='relative inline-block'
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onBlur={handleBlur}>
            <button
                type='button'
                aria-expanded={open}
                aria-controls={panelId}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen((v) => !v)}
                className='border-border text-primary hover:bg-accent focus-visible:ring-ring inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none'>
                <ClipboardList className='size-3.5' />
                체크리스트 기준
            </button>

            {open ? (
                <div
                    id={panelId}
                    role='group'
                    aria-label='체크리스트 기준'
                    className='border-border bg-popover absolute right-0 z-30 mt-1 max-h-72 w-80 overflow-y-auto rounded-md border p-3 shadow-lg'>
                    <div className='mb-2 flex items-center justify-between gap-2'>
                        <p className='text-primary text-xs font-semibold'>
                            체크리스트 기준
                        </p>
                        <span className='text-muted-foreground text-[10px]'>
                            Esc로 닫기
                        </span>
                    </div>
                    {sections.length > 0 ? (
                        <div className='space-y-2'>
                            {sections.map((section) => (
                                <div
                                    key={`${section.title}-${section.content.slice(0, 16)}`}
                                    className='border-border rounded-md border p-2'>
                                    <p className='text-foreground text-xs font-semibold'>
                                        {section.title}
                                    </p>
                                    <p className='text-muted-foreground mt-1 text-xs leading-relaxed whitespace-pre-line'>
                                        {section.content}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='text-muted-foreground text-xs'>
                            등록된 기준 정보가 없습니다.
                        </p>
                    )}
                </div>
            ) : null}
        </div>
    )
}
